import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LoginResponse, User } from '../../shared/models/auth.model';
import { UserLevel } from '../../shared/models/user-level.enum';
import { NotificationService } from '../../shared/services/notification.service';

const AUTH_STORAGE_KEYS = ['token', 'expires_at', 'userDetails'];

// Guards against corrupted/stale-schema localStorage data being cast straight to User —
// role.guard.ts and others call .roles on this without a null check, so a malformed
// stored value would otherwise throw at call time instead of just looking logged-out.
function isUser(value: unknown): value is User {
  return (
    !!value &&
    typeof value === 'object' &&
    typeof (value as User).id === 'string' &&
    typeof (value as User).username === 'string' &&
    Array.isArray((value as User).roles) &&
    (value as User).roles.every((role) => typeof role === 'string')
  );
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly notification: NotificationService,
  ) {}

  // Always hits the real backend — every other endpoint in this app (blower-selection,
  // saved-selections, etc.) is keyed against the real per-account UUID the backend issues
  // here, so a locally-mocked user (e.g. a placeholder id) would silently corrupt those
  // requests instead of just failing login.
  login(username: string, password: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(environment.login.apiEndpoint, { username, password });
  }

  logout(): void {
    const token = localStorage.getItem('token');
    const finishLogout = (): void => {
      this.clearSession();
      this.router.navigateByUrl('/login');
    };
    if (!token) {
      finishLogout();
      return;
    }
    this.http
      .post(environment.login.logout, null, {
        headers: { 'x-auth-token': token },
        observe: 'response',
      })
      .subscribe({
        next: finishLogout,
        error: () => {
          this.notification.showError('LOGIN.ERROR.LOGOUT_REQUEST_ERROR');
          finishLogout();
        },
      });
  }

  isTokenValid(): boolean {
    return this.readValidToken() !== null;
  }

  getToken(): string | null {
    return this.readValidToken();
  }

  // Clears local auth state without calling the backend logout endpoint — used both by
  // logout() (after the backend call resolves) and by the auth interceptor when a 401
  // means the server-side token is no longer valid, so the stale local copy can't linger.
  clearSession(): void {
    try {
      AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
    } catch {
      this.notification.showError('LOGIN.ERROR.LOGOUT_STORAGE_ERROR');
    }
  }

  private readValidToken(): string | null {
    const token = localStorage.getItem('token');
    const expiresAt = localStorage.getItem('expires_at');
    if (!token || !expiresAt) {
      return null;
    }
    let parsedExpiry: unknown;
    try {
      parsedExpiry = JSON.parse(expiresAt);
    } catch {
      return null;
    }
    if (typeof parsedExpiry !== 'number') {
      return null;
    }
    return new Date(parsedExpiry).getTime() > Date.now() ? token : null;
  }

  setUser(userPayload: User): void {
    try {
      localStorage.setItem('userDetails', JSON.stringify(userPayload));
    } catch {
      this.notification.showError('LOGIN.ERROR.PERSIST_USER_ERROR');
    }
  }

  getUserLevel(): UserLevel {
    return UserLevel.resolveForRoles(this.getUser()?.roles ?? []) ?? UserLevel.PARTNER;
  }

  getUser(): User | null {
    const raw = localStorage.getItem('userDetails');
    if (!raw) {
      return null;
    }
    try {
      const parsed: unknown = JSON.parse(raw);
      return isUser(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
}
